import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import omitBy from 'lodash/omitBy';
import React, { useMemo } from 'react';
import { useContext } from 'react';
import { createContext, useRef } from 'react';
const CobStorageContext = /*#__PURE__*/createContext({
  forRelationship: () => ({
    getFromStorage: key => void key,
    setToStorage: noop,
    removeFromStorage: noop,
    clearStorage: noop
  })
});
export const mockStorageImpl = {
  getItem() {
    return null;
  },

  setItem() {},

  removeItem() {}

};
export function CobStorageProvider(_ref) {
  let {
    children,
    impl,
    expirationTimeout = 1000 * 60 * 60 * 24 * 30
    /* 30 days */
    ,
    debounceTime = 100
  } = _ref;
  const storage = impl || localStorage;
  const cachedValue = useRef();
  const cobStorage = useMemo(() => ({
    forRelationship(relationshipId) {
      function getStorage() {
        if (!cachedValue.current) {
          const storageValue = storage.getItem('cob_storage'); // omit expired relationships

          cachedValue.current = omitBy(JSON.parse(storageValue !== null && storageValue !== void 0 ? storageValue : '{}'), ns => ns._expires && ns._expires <= Date.now());
        }

        return cachedValue.current;
      } // in order to prevent sequential calls to local storage, saveStorage will be called after 100ms


      const saveStorage = debounce(() => {
        // set new expiration date
        if (expirationTimeout > 0) {
          getStorageNamespace()['_expires'] = Date.now() + expirationTimeout;
        } // optimize storage by removing empty relationships


        cachedValue.current = omitBy(getStorage(), ns => {
          const keys = Object.keys(ns);
          return keys.length === 0 || keys.length === 1 && keys[0] === '_expires';
        }); // delete storage if all relationships are empty

        if (isEmpty(cachedValue.current)) {
          storage.removeItem('cob_storage');
        } else {
          storage.setItem('cob_storage', JSON.stringify(cachedValue.current));
        }
      }, debounceTime, {
        leading: false,
        trailing: true
      });

      function getStorageNamespace() {
        var _getStorage, _getStorage$relations;

        return (_getStorage$relations = (_getStorage = getStorage())[relationshipId]) !== null && _getStorage$relations !== void 0 ? _getStorage$relations : _getStorage[relationshipId] = {};
      }

      function getFromStorage(key) {
        return getStorageNamespace()[key];
      }

      function setToStorage(key, value) {
        const ns = getStorageNamespace();
        ns[key] = value;
        saveStorage();
      }

      function removeFromStorage(key) {
        const ns = getStorageNamespace();
        delete ns[key];
        saveStorage();
      }

      function clearStorage() {
        const storage = getStorage();
        delete storage[relationshipId];
        saveStorage();
      }

      return {
        getFromStorage,
        setToStorage,
        removeFromStorage,
        clearStorage
      };
    }

  }), [expirationTimeout, storage]);
  return /*#__PURE__*/React.createElement(CobStorageContext.Provider, {
    value: cobStorage
  }, children);
}
export function useCobStorage(relationship) {
  const context = useContext(CobStorageContext);
  return useMemo(() => {
    var _relationship$id;

    return context.forRelationship((_relationship$id = relationship === null || relationship === void 0 ? void 0 : relationship.id) !== null && _relationship$id !== void 0 ? _relationship$id : 'N/A');
  }, [context, relationship === null || relationship === void 0 ? void 0 : relationship.id]);
}